/**
 * The capabilities the assistant will admit to having, in the shape the backend's
 * `registry.menu()` returns: `{id, label, description}`.
 *
 * Held here rather than fetched because the list is three entries long and the ids are the
 * registry's own — the backend revalidates whichever one is sent against the registry and
 * re-applies the permission clamp, so a stale entry here is refused, not obeyed. Swap this for
 * the router's status endpoint the moment the list stops being this short.
 *
 * They are shown as chips on first open rather than as tabs. §8 open decision 3: tabs impose
 * the routing burden on the user on EVERY turn to fix a problem that occurs only on ambiguous
 * ones — and once tabs exist, the single intelligent assistant is gone.
 */
export interface AssistantCapability {
  /** A registered capability id, sent as `options.capabilityId`. */
  id: string
  label: string
  description: string
}

type Translate = (id: string) => string

type AssistantCapabilityGates = {
  /** Attendance board available (hostname/tenant gate). */
  attendanceEnabled: boolean
  /** Knowledge-base board available (`knowledge_base` permission). */
  faqEnabled: boolean
}

/** The capability list for this user, in the order it reads best: data first, then how-to. */
export function getAssistantCapabilities(
  t: Translate,
  {attendanceEnabled, faqEnabled}: AssistantCapabilityGates
): AssistantCapability[] {
  const capabilities: AssistantCapability[] = []

  if (attendanceEnabled) {
    capabilities.push({
      id: 'attendance.data',
      label: t('CHATBOT.CAPABILITY.ATTENDANCE_DATA_LABEL'),
      description: t('CHATBOT.CAPABILITY.ATTENDANCE_DATA_DESCRIPTION'),
    })
  }

  if (faqEnabled) {
    capabilities.push(
      {
        id: 'support.manual',
        label: t('CHATBOT.CAPABILITY.SUPPORT_MANUAL_LABEL'),
        description: t('CHATBOT.CAPABILITY.SUPPORT_MANUAL_DESCRIPTION'),
      },
      {
        id: 'support.walkthrough',
        label: t('CHATBOT.CAPABILITY.SUPPORT_WALKTHROUGH_LABEL'),
        description: t('CHATBOT.CAPABILITY.SUPPORT_WALKTHROUGH_DESCRIPTION'),
      }
    )
  }

  return capabilities
}
